import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Box,
  BreadcrumbGroup,
  Button,
  ButtonDropdown,
  ColumnLayout,
  Container,
  ContentLayout,
  Header as AWSHeader,
  Icon,
  Link,
  SpaceBetween,
  StatusIndicator,
  Table,
} from "@amzn/awsui-components-console";
import { makeApiCall } from "../../utils/api";
import { API_PATHS } from "../../config/api-endpoints";
import { getAndesStatus, ANDES_ERROR_TRUNCATE_LENGTH } from "../../config/andesStatus";
import { useProfile } from "../../hooks/useProfiles";
import StepFunctionsGraph from "../molecules/StepFunctionsGraph";
import JsonDataViewer from "../molecules/JsonDataViewer";
import { cleanMermaidContent } from "../../utils/cleanMermaidContent";
import NProgress from "nprogress";
import {
  JobRunStatus,
  FAILURE_STATUSES,
  IN_PROGRESS_STATUSES,
  isFailureStatus,
  isInProgress,
  RunType,
} from "../../types/jobRunStatus";
import "./JobHistoryOverview.scss";

interface JobRunDetails {
  jobId: string;
  runId: string;
  jobName?: string;
  status: JobRunStatus;
  runType?: RunType;
  startedAt: string;
  updatedAt?: string;
  duration?: string;
  templateId?: string;
  templateUri?: string; // S3 URI of the template
  mermaidDiagram?: string; // Mermaid diagram string from backend
  aiSummary?: string; // AI-generated summary from backend
  createdBy?: string;
  ldap?: string; // LDAP group for this run
  feedback?: string;
  executionArn?: string; // Step Functions execution ARN
  codeGenerationOutput?: {
    // Dynamic map of output data
    sqlPath?: string;
    outputLocation?: string;
    timestamp?: string;
    [key: string]: any; // Allow dynamic properties
  };
  errorMessage?: string | null; // Error message if failed
  currentAttempt?: number; // Current retry attempt number
  executionHistory?: string; // ASL JSON for Step Functions visualization
  executionStatus?: string; // RUNNING | SUCCEEDED | FAILED | TIMED_OUT | ABORTED
  persistenceStatus?: "PERSISTED" | "SKIPPED" | "FAILED";
  persistenceError?: string;
  partitionValue?: string;
  andesRegistrationStatus?:
    | "CREATED"
    | "UNRELEASED"
    | "ACTIVE"
    | "DEPRECATED"
    | "ARCHIVED"
    | "DELETED"
    | "REGISTERED"
    | "FAILED";
  andesRegistrationError?: string;
  preWriteSnapshotId?: string;
  icebergSnapshotId?: string;
  queryExecutionId?: string;
  finalRowCount?: number;
  datasetName?: string;
  dqStatus?: string;
  dqError?: string;
  datasetUrl?: string;
}

interface JobInfo {
  jobId: string;
  jobName: string;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
  ldap?: string;
  jobType?: string;
  mermaidDiagram?: string; // Mermaid diagram from GetJob API
  aiSummary?: string; // AI-generated summary from GetJob API
  artifacts?: {
    // S3 URIs for artifacts
    mermaid?: string;
    summary?: string;
    sql?: string;
  };
  complianceSubmitted?: boolean;
}

interface ExecutonHistory {
  definition: any;
  executionHistory: any;
}

// Helper function to calculate duration between two timestamps
const calculateDuration = (startTime?: string, endTime?: string): string => {
  if (!startTime || !endTime) return "N/A";

  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();
  const durationMs = end - start;

  if (durationMs < 0) return "N/A";

  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

// Truncate a string to maxLen, appending '…' only when actually truncated
const truncate = (value: string, maxLen: number): string => {
  return value.length > maxLen ? value.substring(0, maxLen) + "…" : value;
};

const JobHistoryOverview: React.FC = () => {
  const { id: profileId, jobId, runId } = useParams<{ id: string; jobId: string; runId: string }>();
  const navigate = useNavigate();

  // Fetch profile data from API
  const {
    profile: profileData,
    loading: profileLoading,
    error: profileError,
  } = useProfile(profileId || null);

  const [jobDetails, setJobDetails] = useState<JobRunDetails | null>(null);
  const [jobInfo, setJobInfo] = useState<JobInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [templateData, setTemplateData] = useState<any>(null);
  const [downloadingArtifacts, setDownloadingArtifacts] = useState(false);
  const [downloadingArtifactKey, setDownloadingArtifactKey] = useState<string | null>(null);
  const [copiedRunId, setCopiedRunId] = useState(false);
  const [isRerunning, setIsRerunning] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isAndesRetry, setIsAndesRetry] = useState(false);
  const [publishPollCount, setPublishPollCount] = useState(0);
  const [retryStartedAt, setRetryStartedAt] = useState<string | null>(null);
  const [showFullError, setShowFullError] = useState(false);
  const [fetchedMermaidContent, setFetchedMermaidContent] = useState<string | null>(null);
  const [fetchedAiSummary, setFetchedAiSummary] = useState<string | null>(null);

  // Load job information using GetJob API
  const loadJobInfo = async () => {
    if (!jobId) return;

    try {
      const response = await makeApiCall(API_PATHS.JOBS.GET, "POST", { jobId });
      const responseData = response?.job;

      setJobInfo({
        jobId: responseData.jobId || jobId,
        jobName: responseData.jobName || "Unknown Job",
        status: responseData.status || "UNKNOWN",
        createdBy: responseData.createdBy || "Unknown",
        createdAt: responseData.createdAt || "",
        updatedAt: responseData.updatedAt,
        ldap: responseData.ldap,
        jobType: responseData.jobType,
        mermaidDiagram: responseData.mermaidDiagram,
        artifacts: responseData.artifacts,
        complianceSubmitted: responseData.complianceSubmitted,
      });
    } catch (error: any) {
      console.error("Failed to load job info:", error);
      // Don't block the page if job info fails
    }
  };

  // Load template data from S3 using templateUri
  const loadTemplateData = async (templateUri: string) => {
    try {
      // Get presigned URL for the template
      const { getPresignedDownloadUrl } = await import("../../utils/s3Download");
      const urlResponse = await getPresignedDownloadUrl(templateUri);

      if (!urlResponse?.downloadUrl) {
        console.error("Failed to get presigned URL for template");
        return;
      }

      // Fetch the template content
      const response = await fetch(urlResponse.downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch template: ${response.statusText}`);
      }

      const templateJson = await response.json();

      setTemplateData(templateJson);
    } catch (error: any) {
      console.error("Error loading template data:", error);
      // Don't block the page load if template fails
      setTemplateData(null);
    }
  };

  const loadJobRunDetails = useCallback(
    async (silent = false) => {
      if (!jobId || !runId) {
        setError("Missing job ID or run ID");
        setLoading(false);
        return;
      }

      try {
        if (!silent) {
          setLoading(true);
          NProgress.start();
          setError(null);
        }

        // Call GetJobRunStatus API
        const response = await makeApiCall(API_PATHS.JOBS.RUN_STATUS, "POST", {
          jobId: jobId,
          runId: runId,
        });

        // Map API response to our interface
        const jobRunDetails: JobRunDetails = {
          jobId: response.jobId || jobId,
          runId: response.runId || runId,
          jobName: response.jobName,
          status: response.status || "RUNNING",
          runType: response.runType || "NORMAL",
          startedAt: response.startedAt || new Date().toISOString(),
          updatedAt: response.completedAt,
          duration: calculateDuration(response.startedAt, response.completedAt),
          templateUri: response.templateUri, // Store template S3 URI
          mermaidDiagram: response.mermaidDiagram, // Store Mermaid diagram from backend
          createdBy: response.createdBy,
          ldap: response.ldap,
          feedback: response.feedback || "",
          executionArn: response.executionArn,
          codeGenerationOutput: response.codeGenerationOutput,
          errorMessage: response.errorMessage,
          currentAttempt: response.currentAttempt,
          executionHistory: response.executionHistory,
          executionStatus: response.executionStatus,
          persistenceStatus: response.persistenceStatus,
          persistenceError: response.persistenceError,
          partitionValue: response.partitionValue,
          andesRegistrationStatus: response.andesRegistrationStatus,
          andesRegistrationError: response.andesRegistrationError,
          preWriteSnapshotId: response.preWriteSnapshotId,
          icebergSnapshotId: response.icebergSnapshotId,
          queryExecutionId: response.queryExecutionId,
          finalRowCount: response.finalRowCount,
          datasetName: response.datasetName,
          dqStatus: response.dqStatus,
          dqError: response.dqError,
          datasetUrl: response.datasetUrl,
        };

        setJobDetails(jobRunDetails);
        setFeedback(jobRunDetails.feedback || "");

        // If we have a workflow definition, parse it for visualization
        if (jobRunDetails.executionHistory) {
          try {
            const workflow =
              typeof jobRunDetails.executionHistory === "string"
                ? JSON.parse(jobRunDetails.executionHistory)
                : jobRunDetails.executionHistory;
          } catch (e) {
            console.error("Failed to parse workflow definition:", e);
          }
        }

        // Load template data from S3 if templateUri is available
        if (response.templateUri) {
          await loadTemplateData(response.templateUri);
        } else {
          setTemplateData(null);
        }

        // Fetch mermaid and AI summary content from S3 via job run codeGenerationOutput
        const codeGenOutput = response.codeGenerationOutput;
        if (codeGenOutput) {
          const { getPresignedDownloadUrl } = await import("../../utils/s3Download");

          // Fetch mermaid diagram content from S3
          if (codeGenOutput.mermaid) {
            try {
              const mermaidUri = codeGenOutput.mermaid;
              const match = mermaidUri.match(/^s3:\/\/[^\/]+\/(.+)$/);
              if (match && match[1]) {
                const urlResponse = await getPresignedDownloadUrl(match[1]);
                if (urlResponse?.downloadUrl) {
                  const mermaidResponse = await fetch(urlResponse.downloadUrl, {
                    mode: "cors",
                    headers: { Accept: "text/plain, */*" },
                  });
                  if (mermaidResponse.ok) {
                    const mermaidContent = await mermaidResponse.text();
                    setFetchedMermaidContent(
                      mermaidContent ? cleanMermaidContent(mermaidContent) : null,
                    );
                  }
                }
              }
            } catch (e: any) {
              console.warn("Failed to fetch mermaid content from S3:", e.message || e);
            }
          }

          // Fetch AI summary content from S3
          if (codeGenOutput.summary) {
            try {
              const summaryUri = codeGenOutput.summary;
              const match = summaryUri.match(/^s3:\/\/[^\/]+\/(.+)$/);
              if (match && match[1]) {
                const urlResponse = await getPresignedDownloadUrl(match[1]);
                if (urlResponse?.downloadUrl) {
                  const summaryResponse = await fetch(urlResponse.downloadUrl, {
                    mode: "cors",
                    headers: { Accept: "text/plain, */*" },
                  });
                  if (summaryResponse.ok) {
                    const summaryContent = await summaryResponse.text();
                    setFetchedAiSummary(summaryContent);
                  }
                }
              }
            } catch (e: any) {
              console.warn("Failed to fetch AI summary content from S3:", e.message || e);
            }
          }
        }
      } catch (error: any) {
        console.error("Failed to load job run details:", error);
        if (!silent) {
          setError(error.message || "Failed to load job run details");
        }
      } finally {
        if (!silent) {
          setLoading(false);
          NProgress.done();
        }
      }
    },
    [jobId, runId],
  );

  // Initial load — placed after loadJobRunDetails declaration to avoid TDZ
  useEffect(() => {
    loadJobInfo();
    loadJobRunDetails();
  }, [jobId, runId, loadJobRunDetails]);

  // Auto-poll every 10s while the run is in progress
  useEffect(() => {
    if (!jobDetails?.status || !isInProgress(jobDetails.status)) return;

    let active = true;

    const poll = () => {
      if (!active) return;
      loadJobRunDetails(true).then(() => {
        if (active) {
          setTimeout(poll, 10000);
        }
      });
    };

    const timerId = setTimeout(poll, 10000);

    return () => {
      active = false;
      clearTimeout(timerId);
    };
  }, [jobDetails?.status, loadJobRunDetails]);

  const getOverallStatus = (status: JobRunStatus) => {
    // Check if it's a failure status
    if (isFailureStatus(status)) {
      return <StatusIndicator type="error">{status.replace(/_/g, " ")}</StatusIndicator>;
    }

    // Handle specific statuses
    switch (status) {
      case "COMPLETED":
        return <StatusIndicator type="success">Completed</StatusIndicator>;
      case "SUCCEEDED":
        return <StatusIndicator type="success">Completed</StatusIndicator>;
      case "PENDING":
        return <StatusIndicator type="pending">Pending</StatusIndicator>;
      case "GENERATING_CODE":
        return <StatusIndicator type="loading">Generating Code</StatusIndicator>;
      case "SUBMITTING_QUERY":
        return <StatusIndicator type="loading">Submitting Query</StatusIndicator>;
      case "QUERY_SUBMITTED":
        return <StatusIndicator type="info">Query Submitted</StatusIndicator>;
      case "QUERY_RUNNING":
        return <StatusIndicator type="in-progress">Query Running</StatusIndicator>;
      case "CODE_RETRIEVED":
        return <StatusIndicator type="success">Code Retrieved</StatusIndicator>;
      case "RETRYING":
        return <StatusIndicator type="warning">Retrying</StatusIndicator>;
      case "PERSISTING":
        return <StatusIndicator type="in-progress">Writing to Iceberg</StatusIndicator>;
      case "VERIFYING":
        return <StatusIndicator type="loading">Verifying Data</StatusIndicator>;
      case "PUBLISHING":
        return <StatusIndicator type="loading">Publishing to Andes</StatusIndicator>;
      default:
        return <StatusIndicator type="pending">{status}</StatusIndicator>;
    }
  };

  const getBadgeColor = (status: JobRunStatus) => {
    // Failure statuses - red
    if (isFailureStatus(status)) {
      return "red";
    }

    // Success statuses - green
    if (status === "COMPLETED" || status === "SUCCEEDED" || status === "CODE_RETRIEVED") {
      return "green";
    }

    // In-progress/loading statuses - blue
    if (
      status === "GENERATING_CODE" ||
      status === "SUBMITTING_QUERY" ||
      status === "QUERY_RUNNING" ||
      status === "PERSISTING" ||
      status === "VERIFYING" ||
      status === "PUBLISHING"
    ) {
      return "blue";
    }

    // Retrying status - orange/yellow
    if (status === "RETRYING") {
      return "blue"; // AWS UI doesn't have orange, use blue
    }

    // Pending/submitted statuses - grey
    return "grey";
  };

  // Copy run ID to clipboard
  const handleCopyRunId = () => {
    if (runId) {
      navigator.clipboard.writeText(runId);
      setCopiedRunId(true);
      setTimeout(() => setCopiedRunId(false), 2000);
    }
  };

  // Get truncated error message (first 5 lines or 300 characters)
  const getTruncatedError = (errorMessage: string) => {
    if (!errorMessage) {
      return { truncated: "", hasMore: false };
    }

    const lines = errorMessage.split("\n");

    // Check if message is long (more than 5 lines OR more than 300 characters)
    if (lines.length > 5) {
      return {
        truncated: lines.slice(0, 5).join("\n"),
        hasMore: true,
      };
    }

    // If single/few lines but very long text
    if (errorMessage.length > 300) {
      return {
        truncated: errorMessage.substring(0, 300) + "...",
        hasMore: true,
      };
    }

    return { truncated: errorMessage, hasMore: false };
  };

  // Handle rerun job with ADHOC run type
  const handleRerunJob = async () => {
    if (!jobId) {
      console.error("No job ID available for rerun");
      return;
    }

    setIsRerunning(true);
    setError(null);
    NProgress.start();

    try {
      const runJobPayload = {
        jobId,
        runType: "ADHOC",
      };

      const response = await makeApiCall(API_PATHS.JOBS.RUN, "POST", runJobPayload);

      // Navigate to the new run or refresh to see it
      const runId = response.runId || null;
      if (runId) {
        navigate(`/profile/${profileId}/job/${jobId}/run/${runId}`);
      } else {
        // If no runId in response, refresh to show the new run in the list
        window.location.reload();
      }
    } catch (error: any) {
      console.error("Error rerunning job:", error);

      // Extract the actual error message from the response
      let errorMessage = "Failed to rerun job. Please try again.";

      if (error.message) {
        errorMessage = error.message;
      }

      // Set error to display in alert at top of page
      setError(errorMessage);
    } finally {
      setIsRerunning(false);
      NProgress.done();
    }
  };

  // Handle publish to Iceberg storage
  // Shared publish/retry flow — only the isRetry flag differs.
  const startPublishFlow = async (isRetry: boolean) => {
    if (!jobId || !runId) {
      console.error(`No job ID or run ID available for ${isRetry ? "Andes retry" : "publish"}`);
      return;
    }

    setIsPublishing(true);
    setIsAndesRetry(isRetry);
    if (isRetry) {
      // Don't capture the pre-call timestamp — the backend may update updatedAt
      // when it receives the request, causing premature polling termination.
      // Instead, the baseline will be captured from the first poll response.
      setRetryStartedAt(null);
    }
    setPublishPollCount(0);
    setError(null);
    NProgress.start();

    try {
      await makeApiCall(API_PATHS.JOBS.RUN, "POST", { jobId, runId, runType: "PUBLISH" });
      await loadJobRunDetails(true);
    } catch (error: any) {
      const action = isRetry ? "retry Andes registration" : "publish dataset";
      console.error(`Error: ${action}:`, error);
      setError(error.message || `Failed to ${action}. Please try again.`);
      setIsPublishing(false);
      if (isRetry) setIsAndesRetry(false);
      NProgress.done();
    }
  };

  const handlePublish = () => startPublishFlow(false);
  const handleRetryAndes = () => startPublishFlow(true);

  // Poll while publish is in progress — stops when persistenceStatus resolves
  // or after MAX_PUBLISH_POLLS (5 min at 5s intervals).
  // For Andes retry, polls until andesRegistrationStatus leaves FAILED.
  const MAX_PUBLISH_POLLS = 60;
  useEffect(() => {
    if (!isPublishing) return;

    if (isAndesRetry) {
      // Andes retry: persistenceStatus is already PERSISTED.
      // Capture the baseline updatedAt from the first poll response after the
      // retry is initiated, then compare subsequent polls against that baseline.
      if (!retryStartedAt && jobDetails?.updatedAt) {
        setRetryStartedAt(jobDetails.updatedAt);
        return; // wait for next poll to detect a change
      }
      const andes = jobDetails?.andesRegistrationStatus;
      const updatedSinceRetry =
        retryStartedAt && jobDetails?.updatedAt && jobDetails.updatedAt !== retryStartedAt;
      if (andes && (andes !== "FAILED" || updatedSinceRetry)) {
        setIsPublishing(false);
        setIsAndesRetry(false);
        setPublishPollCount(0);
        setRetryStartedAt(null);
        NProgress.done();
        return;
      }
    } else {
      // Normal publish: stop when persistenceStatus resolves
      const ps = jobDetails?.persistenceStatus;
      if (ps === "PERSISTED" || ps === "FAILED" || ps === "SKIPPED") {
        setIsPublishing(false);
        setPublishPollCount(0);
        NProgress.done();
        return;
      }
    }

    if (publishPollCount >= MAX_PUBLISH_POLLS) {
      setIsPublishing(false);
      setIsAndesRetry(false);
      setPublishPollCount(0);
      NProgress.done();
      setError(
        isAndesRetry
          ? "Andes registration retry is taking longer than expected. Please refresh the page to check the latest status."
          : "Publish is taking longer than expected. Please refresh the page to check the latest status.",
      );
      return;
    }

    let active = true;
    const timerId = setTimeout(() => {
      if (active) {
        setPublishPollCount((c) => c + 1);
        loadJobRunDetails(true);
      }
    }, 5000);

    return () => {
      active = false;
      clearTimeout(timerId);
    };
  }, [
    isPublishing,
    isAndesRetry,
    retryStartedAt,
    jobDetails?.persistenceStatus,
    jobDetails?.andesRegistrationStatus,
    jobDetails?.updatedAt,
    loadJobRunDetails,
    publishPollCount,
  ]);

  // Check if publish button should be shown
  const canPublish =
    jobDetails?.status === "SUCCEEDED" && jobDetails?.persistenceStatus !== "PERSISTED" && jobInfo != null && jobInfo.complianceSubmitted === true;

  // Check if Andes retry button should be shown
  const canRetryAndes =
    jobDetails?.status === "SUCCEEDED" &&
    jobDetails?.persistenceStatus === "PERSISTED" &&
    jobDetails?.andesRegistrationStatus === "FAILED";

  // Generate dropdown items from codeGenerationOutput
  const getArtifactDropdownItems = () => {
    if (!jobDetails?.codeGenerationOutput) return [];

    const output = jobDetails.codeGenerationOutput;
    const items = [];

    // Iterate through all keys in codeGenerationOutput
    for (const [key, value] of Object.entries(output)) {
      // Only include string values that look like S3 paths or file paths
      if (typeof value === "string" && (value.startsWith("s3://") || value.includes("/"))) {
        items.push({
          id: key,
          text: key, // Use actual key from API response
          disabled: downloadingArtifactKey === key,
        });
      }
    }

    return items;
  };

  const handleDownloadArtifact = async (artifactKey: string, artifactValue: string) => {
    if (!artifactValue) {
      return;
    }

    try {
      setDownloadingArtifactKey(artifactKey);

      // Get presigned URL for the artifact
      const { getPresignedDownloadUrl } = await import("../../utils/s3Download");

      // Extract S3 key from URI (handle both s3:// URIs and direct keys)
      let s3Key: string;
      if (artifactValue.startsWith("s3://")) {
        const match = artifactValue.match(/^s3:\/\/[^\/]+\/(.+)$/);
        if (!match || !match[1]) {
          throw new Error("Invalid S3 URI format");
        }
        s3Key = match[1];
      } else {
        s3Key = artifactValue;
      }

      const urlResponse = await getPresignedDownloadUrl(s3Key);

      if (!urlResponse?.downloadUrl) {
        throw new Error("Failed to get presigned URL for artifact");
      }

      // Fetch the artifact content
      const response = await fetch(urlResponse.downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch artifact: ${response.statusText}`);
      }

      const blob = await response.blob();

      // Get the file extension from the S3 path or default based on content type
      const pathParts = artifactValue.split("/");
      const fileName = pathParts[pathParts.length - 1];
      let downloadFileName: string;

      if (fileName.includes(".")) {
        // Use the original filename if it has an extension
        downloadFileName = fileName;
      } else {
        // Determine extension from content type or default to .csv
        const contentType = response.headers.get("content-type") || "";
        let extension = ".csv";
        if (contentType.includes("json")) extension = ".json";
        else if (contentType.includes("xlsx")) extension = ".xlsx";
        else if (contentType.includes("csv")) extension = ".csv";
        else if (contentType.includes("sql")) extension = ".sql";

        downloadFileName = `${artifactKey}_${runId?.substring(0, 8) || "download"}${extension}`;
      }

      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error: any) {
      console.error(`Failed to download artifact ${artifactKey}:`, error);
      setError(error.message || `Failed to download ${artifactKey}`);
    } finally {
      setDownloadingArtifactKey(null);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!jobDetails?.templateUri) {
      return;
    }

    try {
      NProgress.start();

      // Get presigned URL for the template
      const { getPresignedDownloadUrl } = await import("../../utils/s3Download");

      // Extract S3 key from URI
      const match = jobDetails.templateUri.match(/^s3:\/\/[^\/]+\/(.+)$/);
      if (!match || !match[1]) {
        throw new Error("Invalid S3 URI format");
      }

      const urlResponse = await getPresignedDownloadUrl(match[1]);

      if (!urlResponse?.downloadUrl) {
        throw new Error("Failed to get presigned URL for template");
      }

      // Fetch the template content
      const response = await fetch(urlResponse.downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch template: ${response.statusText}`);
      }

      const blob = await response.blob();

      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `template-run-${runId || "download"}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error: any) {
      console.error("Failed to download template:", error);
      setError(error.message || "Failed to download template");
    } finally {
      NProgress.done();
    }
  };

  if (loading) {
    return (
      <ContentLayout
        defaultPadding={true}
        headerVariant="high-contrast"
        header={
          <Box padding={{ horizontal: "xxxl", vertical: "s" }}>
            <SpaceBetween size="m">
              <AWSHeader variant="h1">Loading...</AWSHeader>
            </SpaceBetween>
          </Box>
        }
      >
        <Box padding={{ horizontal: "xxxl" }}>
          <Container>
            <Box padding="xl" textAlign="center">
              <StatusIndicator type="loading">Loading job details...</StatusIndicator>
            </Box>
          </Container>
        </Box>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      defaultPadding={false}
      headerVariant="high-contrast"
      header={
        <Box padding={{ horizontal: "xxxl", vertical: "s" }}>
          <SpaceBetween size="m" direction="vertical">
            <BreadcrumbGroup
              items={[
                { text: "Home", href: "/" },
                { text: "Profiles", href: "/" },
                { text: profileData?.name || "Loading...", href: `/profile/${profileId}` },
                { text: "Jobs", href: `/profile/${profileId}/jobs` },
                {
                  text: jobDetails?.jobName || "Job",
                  href: `/profile/${profileId}/job/${jobId}`,
                },
                { text: "History", href: `/profile/${profileId}/job/${jobId}/history` },
                {
                  text: `Run ${jobDetails?.runId?.substring(0, 8) || runId?.substring(0, 8) || ""}...`,
                  href: "#",
                },
              ]}
              ariaLabel="Breadcrumbs"
            />
            <Box>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "8px",
                    }}
                  >
                    <Box fontSize="heading-xl" fontWeight="bold" color="inherit">
                      {jobInfo?.jobName ? `Job @${jobInfo?.jobName}` : "Loading..."}
                    </Box>
                    {jobDetails && (
                      <Badge color={getBadgeColor(jobDetails.status)}>{jobDetails.status}</Badge>
                    )}
                  </div>
                  <Box fontSize="body-m" color="inherit" margin={{ top: "xs" }}>
                    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                      {jobDetails?.ldap && (
                        <>
                          <Badge color="green">{jobDetails.ldap}</Badge>
                          <Box color="text-status-inactive">|</Box>
                        </>
                      )}
                      {jobDetails?.runType && (
                        <>
                          <Badge color="blue">{jobDetails.runType}</Badge>
                          <Box color="text-status-inactive">|</Box>
                        </>
                      )}
                      {jobDetails?.andesRegistrationStatus && (
                        <>
                          <Badge color={getAndesStatus(jobDetails.andesRegistrationStatus).color}>
                            {`Andes ${getAndesStatus(jobDetails.andesRegistrationStatus).label || jobDetails.andesRegistrationStatus}`}
                          </Badge>
                          {jobDetails.andesRegistrationError && (
                            <Box fontSize="body-s" color="text-status-error">
                              {jobDetails.andesRegistrationError.length <=
                              ANDES_ERROR_TRUNCATE_LENGTH ? (
                                jobDetails.andesRegistrationError
                              ) : (
                                <>
                                  {jobDetails.andesRegistrationError.substring(
                                    0,
                                    ANDES_ERROR_TRUNCATE_LENGTH,
                                  ) + "…"}{" "}
                                  <Link
                                    href="#"
                                    onFollow={(e) => {
                                      e.preventDefault();
                                      setShowFullError(true);
                                      document
                                        .getElementById("andes-registration-detail")
                                        ?.scrollIntoView({ behavior: "smooth" });
                                    }}
                                    fontSize="body-s"
                                  >
                                    See details
                                  </Link>
                                </>
                              )}
                            </Box>
                          )}
                          <Box color="text-status-inactive">|</Box>
                        </>
                      )}
                      <Box color="text-body-secondary">Run ID: {runId?.substring(0, 8)}...</Box>
                      <Button
                        variant="inline-icon"
                        iconName={copiedRunId ? "check" : "copy"}
                        onClick={handleCopyRunId}
                        ariaLabel="Copy run ID"
                      />
                      <Box color="text-status-inactive">|</Box>
                      <Box color="text-body-secondary">
                        Duration: {jobDetails?.duration ? jobDetails.duration : "N/A"}
                      </Box>
                      <Box color="text-status-inactive">|</Box>
                      <Box color="text-body-secondary">
                        Started:{" "}
                        {jobDetails?.startedAt
                          ? new Date(jobDetails.startedAt).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "N/A"}
                      </Box>
                    </SpaceBetween>
                  </Box>
                </div>
                <SpaceBetween direction="horizontal" size="xs">
                  <ButtonDropdown
                    items={getArtifactDropdownItems()}
                    onItemClick={({ detail }) => {
                      const artifactKey = detail.id;
                      const artifactValue = jobDetails?.codeGenerationOutput?.[artifactKey];
                      if (artifactValue && typeof artifactValue === "string") {
                        handleDownloadArtifact(artifactKey, artifactValue);
                      }
                    }}
                    disabled={
                      !jobDetails?.codeGenerationOutput ||
                      getArtifactDropdownItems().length === 0 ||
                      downloadingArtifactKey !== null
                    }
                    loading={downloadingArtifactKey !== null}
                  >
                    {downloadingArtifactKey
                      ? `Downloading ${downloadingArtifactKey}...`
                      : "Download artifacts"}
                  </ButtonDropdown>
                  <Button
                    iconName="refresh"
                    variant="normal"
                    onClick={() => loadJobRunDetails()}
                    loading={loading}
                  ></Button>
                  <Button
                    iconName="redo"
                    variant="primary"
                    onClick={handleRerunJob}
                    loading={isRerunning}
                    disabled={isRerunning || isPublishing}
                  >
                    Rerun
                  </Button>
                  {canPublish && (
                    <Button
                      iconName="upload"
                      variant="normal"
                      onClick={handlePublish}
                      loading={isPublishing}
                      disabled={isPublishing || isRerunning}
                    >
                      Publish to Andes
                    </Button>
                  )}
                  {canRetryAndes && (
                    <Button
                      iconName="refresh"
                      variant="normal"
                      onClick={handleRetryAndes}
                      loading={isPublishing}
                      disabled={isPublishing || isRerunning}
                    >
                      Retry Andes Registration
                    </Button>
                  )}
                  {!canPublish && !canRetryAndes && (
                    <Button iconName="upload" variant="normal" disabled>
                      Publish to Andes
                    </Button>
                  )}
                </SpaceBetween>
              </div>
            </Box>
          </SpaceBetween>
        </Box>
      }
    >
      <div
        className="job-history-content"
        style={{ height: "calc(100vh - 180px)", overflowY: "auto", overflowX: "hidden" }}
      >
        <Box padding={{ horizontal: "xxxl", vertical: "l", bottom: "xl" }}>
          <SpaceBetween size="l">
            {/* Error Alert */}
            {error && (
              <Alert type="warning" dismissible onDismiss={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Compliance questionnaire required note */}
            {jobDetails?.status === "SUCCEEDED" && jobDetails?.persistenceStatus !== "PERSISTED" && jobInfo != null && !jobInfo.complianceSubmitted && (
              <Alert type="warning" header="Compliance Questionnaire Required">
                You must submit the SOX compliance questionnaire before publishing to Andes or enabling schedules.
                Please go to the{" "}
                <Link
                  onFollow={(e) => {
                    e.preventDefault();
                    navigate(`/profile/${profileId}/job/${jobId}`);
                  }}
                >
                  Job Overview page
                </Link>{" "}
                to complete the questionnaire in the Review Status section.
              </Alert>
            )}

            {/* Feedback Information Banner */}
            <Alert
              type="info"
              dismissible={false}
              header="Code Feedback"
              action={
                <Button
                  variant="primary"
                  iconName="edit"
                  onClick={() => {
                    console.log("Navigating to:", `/profile/${profileId}/job/${jobId}`);
                    navigate(`/profile/${profileId}/job/${jobId}`);
                  }}
                >
                  Provide Feedback
                </Button>
              }
            >
              Not satisfied with the job run results? You can improve the generated code by
              providing feedback.
            </Alert>

            {/* Job Overview Section */}
            <Container header={<AWSHeader variant="h2">Job run overview</AWSHeader>}>
              <SpaceBetween size="l">
                {/* Show error message if job failed */}
                {jobDetails?.errorMessage &&
                  jobDetails?.status &&
                  isFailureStatus(jobDetails.status) &&
                  (() => {
                    const { truncated, hasMore } = getTruncatedError(jobDetails.errorMessage);
                    return (
                      <Alert
                        type="error"
                        action={
                          hasMore ? (
                            <Button onClick={() => setShowFullError(!showFullError)} variant="link">
                              {showFullError ? "Show less" : "Show more"}
                            </Button>
                          ) : undefined
                        }
                      >
                        <div>
                          <strong>Job Failed:</strong>
                          <pre
                            style={{
                              marginTop: "8px",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              fontFamily: "Monaco, Menlo, monospace",
                              fontSize: "12px",
                              lineHeight: "1.5",
                            }}
                          >
                            {showFullError ? jobDetails.errorMessage : truncated}
                          </pre>
                        </div>
                      </Alert>
                    );
                  })()}

                <ColumnLayout columns={2} variant="text-grid">
                  <SpaceBetween size="l">
                    <div>
                      <Box variant="awsui-key-label">Start Time</Box>
                      <div>
                        {jobDetails?.startedAt
                          ? new Date(jobDetails.startedAt).toLocaleString()
                          : "N/A"}
                      </div>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">End Time</Box>
                      <div>
                        {jobDetails?.updatedAt
                          ? new Date(jobDetails.updatedAt).toLocaleString()
                          : "N/A"}
                      </div>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Created By</Box>
                      <div>
                        {jobDetails?.createdBy ? (
                          <Link
                            href={`https://phonetool.amazon.com/users/${jobDetails.createdBy}`}
                            external={true}
                          >
                            @{jobDetails.createdBy}
                          </Link>
                        ) : (
                          "N/A"
                        )}
                      </div>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">LDAP Group</Box>
                      <div>
                        {jobDetails?.ldap ? <Badge color="green">{jobDetails.ldap}</Badge> : "N/A"}
                      </div>
                    </div>
                  </SpaceBetween>
                  <SpaceBetween size="l">
                    <div>
                      <Box variant="awsui-key-label">Status</Box>
                      <div>{jobDetails && getOverallStatus(jobDetails.status)}</div>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Duration</Box>
                      <div>{jobDetails?.duration || "N/A"}</div>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Step Function Execution Status</Box>
                      <div>{jobDetails?.executionStatus || "N/A"}</div>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Retry Attempt</Box>
                      <div>
                        {jobDetails?.currentAttempt ? `${jobDetails.currentAttempt}` : "N/A"}
                      </div>
                    </div>
                  </SpaceBetween>
                </ColumnLayout>
              </SpaceBetween>
            </Container>

            {/* Data Quality Section */}
            <Container
              header={
                <AWSHeader variant="h2" description="Data quality checks for this job run">
                  Data Quality (DQ)
                </AWSHeader>
              }
            >
              <Table
                columnDefinitions={[
                  {
                    id: "check",
                    header: "Check",
                    cell: (item: { check: string; status: string; detail?: string }) => item.check,
                    width: 250,
                  },
                  {
                    id: "status",
                    header: "Status",
                    cell: (item: { check: string; status: string; detail?: string }) => {
                      if (item.status === "PASSED")
                        return <StatusIndicator type="success">Passed</StatusIndicator>;
                      if (item.status === "FAILED")
                        return <StatusIndicator type="error">Failed</StatusIndicator>;
                      if (item.status === "PENDING")
                        return <StatusIndicator type="pending">Pending</StatusIndicator>;
                      if (item.status === "NOT_RUN")
                        return <StatusIndicator type="stopped">Not Run</StatusIndicator>;
                      if (item.status === "TBD")
                        return <StatusIndicator type="stopped">TBD</StatusIndicator>;
                      return <StatusIndicator type="info">{item.status}</StatusIndicator>;
                    },
                    width: 200,
                  },
                  {
                    id: "detail",
                    header: "Details",
                    cell: (item: { check: string; status: string; detail?: string }) =>
                      item.detail || "-",
                    width: 400,
                  },
                ]}
                items={[
                  {
                    check: "Data Quality Status",
                    status: jobDetails?.dqStatus || "TBD",
                    detail: jobDetails?.dqError || "",
                  },
                ]}
                variant="embedded"
                wrapLines
              />
            </Container>

            {/* Dataset Details — always shown */}
            {
              <Container header={<AWSHeader variant="h2">Published Dataset</AWSHeader>}>
                <SpaceBetween size="l">
                  {/* Persistence failure alert — following StrategyControlHub pattern */}
                  {jobDetails?.persistenceStatus === "FAILED" && jobDetails?.persistenceError && (
                    <Alert type="error" header="Dataset persistence failed">
                      <pre
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          fontFamily: "Monaco, Menlo, monospace",
                          fontSize: "12px",
                          lineHeight: "1.5",
                          margin: 0,
                        }}
                      >
                        {jobDetails.persistenceError}
                      </pre>
                    </Alert>
                  )}
                  {jobDetails?.persistenceStatus === "FAILED" && !jobDetails?.persistenceError && (
                    <Alert type="error">
                      Dataset persistence failed. Check the Step Functions execution flow below for
                      details.
                    </Alert>
                  )}
                  <ColumnLayout columns={2} variant="text-grid">
                    <SpaceBetween size="l">
                      <div>
                        <Box variant="awsui-key-label">Persistence Status</Box>
                        <div>
                          {jobDetails?.persistenceStatus === "PERSISTED" && (
                            <StatusIndicator type="success">Persisted</StatusIndicator>
                          )}
                          {jobDetails?.persistenceStatus === "FAILED" && (
                            <StatusIndicator type="error">Failed</StatusIndicator>
                          )}
                          {jobDetails?.persistenceStatus === "SKIPPED" && (
                            <StatusIndicator type="stopped">Skipped</StatusIndicator>
                          )}
                          {!jobDetails?.persistenceStatus && (
                            <Box color="text-status-inactive">N/A</Box>
                          )}
                        </div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Dataset</Box>
                        <div>
                          {jobDetails?.datasetUrl ? (
                            <Link href={jobDetails.datasetUrl} external={true}>
                              {jobDetails.datasetUrl}
                            </Link>
                          ) : (
                            "N/A"
                          )}
                        </div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Partition</Box>
                        <div>{jobDetails?.partitionValue || "N/A"}</div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Rows Written</Box>
                        <div>
                          {jobDetails?.finalRowCount != null
                            ? jobDetails.finalRowCount.toLocaleString()
                            : "N/A"}
                        </div>
                      </div>
                    </SpaceBetween>
                    <SpaceBetween size="l">
                      <div id="andes-registration-detail">
                        <Box variant="awsui-key-label">Andes Registration</Box>
                        <div>
                          {jobDetails?.andesRegistrationStatus ? (
                            <StatusIndicator
                              type={
                                getAndesStatus(jobDetails.andesRegistrationStatus).indicatorType
                              }
                            >
                              {getAndesStatus(jobDetails.andesRegistrationStatus).label ||
                                jobDetails.andesRegistrationStatus}
                            </StatusIndicator>
                          ) : (
                            <Box color="text-status-inactive">N/A</Box>
                          )}
                          {jobDetails?.andesRegistrationError && (
                            <Box
                              margin={{ top: "xxs" }}
                              fontSize="body-s"
                              color="text-status-error"
                            >
                              {jobDetails.andesRegistrationError.length <=
                              ANDES_ERROR_TRUNCATE_LENGTH ? (
                                jobDetails.andesRegistrationError
                              ) : (
                                <>
                                  {showFullError
                                    ? jobDetails.andesRegistrationError
                                    : jobDetails.andesRegistrationError.substring(
                                        0,
                                        ANDES_ERROR_TRUNCATE_LENGTH,
                                      ) + "…"}{" "}
                                  <Link
                                    href="#"
                                    onFollow={(e) => {
                                      e.preventDefault();
                                      setShowFullError(!showFullError);
                                    }}
                                    fontSize="body-s"
                                  >
                                    {showFullError ? "Show less" : "Show more"}
                                  </Link>
                                </>
                              )}
                            </Box>
                          )}
                        </div>
                      </div>
                    </SpaceBetween>
                  </ColumnLayout>
                </SpaceBetween>
              </Container>
            }

            {/* AWS Step Functions Visualization */}
            <Container header={<AWSHeader variant="h2">Execution Flow</AWSHeader>}>
              <Box padding="s" textAlign={"center"}>
                {jobDetails?.executionHistory ? (
                  (() => {
                    try {
                      // Parse the executionHistory JSON string
                      const parsedHistory: ExecutonHistory =
                        typeof jobDetails.executionHistory === "string"
                          ? JSON.parse(jobDetails.executionHistory)
                          : jobDetails.executionHistory;

                      // Extract definition and stateStatuses
                      const definition = parsedHistory?.definition;
                      const historyEvents = parsedHistory.executionHistory;

                      if (!definition) {
                        return (
                          <Box textAlign="center" color="text-status-inactive" padding="s">
                            <p>Workflow definition not available in execution history.</p>
                          </Box>
                        );
                      }

                      return (
                        <StepFunctionsGraph
                          historyEvents={historyEvents}
                          stateMachineDefinition={definition}
                        />
                      );
                    } catch (error) {
                      return (
                        <Alert type="error">
                          Failed to parse workflow execution history:{" "}
                          {error instanceof Error ? error.message : "Unknown error"}
                        </Alert>
                      );
                    }
                  })()
                ) : (
                  <Box textAlign="center" color="text-status-inactive" padding="xl">
                    <p>Workflow definition not available. Job may still be processing.</p>
                  </Box>
                )}
              </Box>
            </Container>

            {/* AI Analysis Section - Mermaid Diagram and AI Summary */}
            {(fetchedMermaidContent ||
              fetchedAiSummary ||
              jobInfo?.mermaidDiagram ||
              jobInfo?.aiSummary) && (
              <Container header={<AWSHeader variant="h2">AI Analysis</AWSHeader>}>
                <JsonDataViewer
                  data={{}}
                  mermaidDiagram={fetchedMermaidContent || jobInfo?.mermaidDiagram}
                  aiSummary={fetchedAiSummary || jobInfo?.aiSummary}
                  defaultView="summary"
                  style={{
                    maxHeight: "600px",
                  }}
                  collapsed={false}
                  displayObjectSize={false}
                  enableClipboard={true}
                />
              </Container>
            )}
          </SpaceBetween>
          <Box padding={{ horizontal: "xxxl", vertical: "l", bottom: "xl" }}></Box>
        </Box>
      </div>
    </ContentLayout>
  );
};

export default JobHistoryOverview;
